import { autoinject, computedFrom, transient } from "aurelia-framework";
import { Router } from "aurelia-router";
import { EntityTypeConfigurationModel, EntityTypeConfigurationHelper } from "../../../../models/basics/basics.models";
import { EntityFinderSearch } from "../../../components/entity-finder/entity-finder-search";
import { EntityValidationsService, ClientSideValidator } from "../../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum, RulesHolder } from "../../../../client-validation-system/validations.models";
import { NewCfrRequest, CfrVettingConditionEnum } from "../../../../models/cfr/cfr.models";
import { CfrService } from "../../../../services/cfr/cfr.service";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { LinkedIncidentModel, LinkedPscInspectionModel } from "../../../../models/fbrs/fbr.models";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { ManageVesselService } from "../../../../services/vessels/manage-vessel.service";
import { VesselDetailModel } from "../../../../models/vessels/vessel.models";
import { LookupValue } from "../../../../models/lookups/lookup.models";
import { RightshipEntityAttachmentModel } from "../../../../models/attachment/attachment.models";
import { Reflection } from "../../../../utilities/reflection";

@autoinject
@transient()
export class NewCfr {

    public vesselId: number;
    public expiryDate: Date;
    public title: string = "";
    public description: string = "";
    public comments: string;
    public businessUnitId: number;
    public customerId: number;
    public cfrCategoryId: number;
    public cfrId: number;
    public isEdit: boolean;
    public currentStep: number = 1;
    public flagForCustomer: boolean = false;
    public flagForAllVets: boolean = true;
    public linkedIncidents: LinkedIncidentModel[] = [];
    public linkedPscInspections: LinkedPscInspectionModel[] = [];
    public incidentEntityTypes: EntityTypeConfigurationModel[];
    public pscInspectionEntityTypes: EntityTypeConfigurationModel[];
    public businessUnitEntityTypes: EntityTypeConfigurationModel[];
    public incidentLinkingVisible: boolean = false;
    public pscInspectionLinkingVisible: boolean = false;
    public incidentIds: number[] = [];
    public pscInspectionIds: number[] = [];
    public businessUnitVM: EntityFinderSearch;
    public vessel: VesselDetailModel;
    public validator: ClientSideValidator;
    public attachments: File[] = [];
    public fbrItemId: number;
    public attachmentsToCopy: RightshipEntityAttachmentModel[];

    constructor(private router: Router,
        private entityValidationsService: EntityValidationsService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private cfrService: CfrService,
        private manageVesselService: ManageVesselService,
        private validationsClientService: ValidationsClientService,
        private notificationClientService: NotificationClientService) {
        this.incidentEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Incidents"]);
        this.pscInspectionEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Pscs"]);
        this.businessUnitEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["BusinessUnits"]);
    }

    onBusinessUnitSelected(bu) {
        if (bu) {
            this.businessUnitId = bu.id;
            this.customerId = bu.customerId;
        }
    }

    private save(saveAttachment: boolean = true) {

        const request: NewCfrRequest = {
            comments: this.comments,
            vesselId: this.vessel?.vesselId,
            cfrId: this.cfrId,
            cfrCategoryId: this.cfrCategoryId,
            businessUnitId: this.businessUnitId,
            description: this.description,
            title: this.title,
            expiryDate: this.expiryDate,
            cfrVettingCondition: this.flagForCustomer ? CfrVettingConditionEnum.FlagForCustomer : CfrVettingConditionEnum.FlagForAllVets,
            attachments: saveAttachment ? this.attachments : [],
            fbrItemId: this.fbrItemId,
            attachmentsToCopy: saveAttachment && this.attachmentsToCopy && this.attachmentsToCopy.length > 0 ? this.attachmentsToCopy.map(x => {
                return {
                    entityAttachmentId: x.entityAttachmentId,
                    fileName: x.name
                }
            }) : []
        };

        return new Promise<void>((resolve, reject) => {
            this.cfrService.save(request).then(response => {
                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                    reject();
                } else {
                    this.cfrId = response.cfrId;
                    resolve();
                }
            }).catch(response => {
                if (response && response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                } else {
                    this.notificationClientService.show("Error submitting Condition for review", "", NotificationType.Error);
                }
                reject();
            });
        });
    }

    async activate(params: {
        vesselId: number,
        cfrId: number,
        fbrItemId: number,
        businessUnitId: number,
        customerId: number,
        description: string,
        title: string,
        attachmentsToCopy: RightshipEntityAttachmentModel[]
    }) {
        this.vesselId = Number(params.vesselId);
        this.fbrItemId = Number(params.fbrItemId);

        this.manageVesselService.getVessel({
            vesselId: this.vesselId,
            imo: null,
            bKey: null
        }).then(response => {
            this.vessel = response.vessel;
        });

        if (params.cfrId) {

            const response = await this.cfrService.getCfr(params.cfrId);
            this.linkedPscInspections = response.linkedPscInspections;
            this.linkedIncidents = response.linkedIncidents;
            this.comments = response.comments;
            this.cfrId = response.cfrId;
            this.isEdit = response.cfrId > 0;
            this.cfrCategoryId = response.cfrCategoryId;
            this.customerId = response.customerId;
            this.businessUnitId = response.businessUnitId;
            this.description = response.description;
            this.title = response.title;
            this.expiryDate = response.expiryDate;
            this.flagForAllVets = response.flagForAllVets;
            this.flagForCustomer = response.flagForCustomer;
        }
        else if (params.fbrItemId > 0) {
            this.customerId = params.customerId;
            this.businessUnitId = params.businessUnitId;
            this.description = params.description;
            this.title = params.title;
            this.attachmentsToCopy = params.attachmentsToCopy;
        }
    }

    attached() {
        if (this.businessUnitId) {
            this.businessUnitVM.set({ id: this.businessUnitId, entityTypeUri: "BusinessUnits" });
        }
        const rulesHolder: RulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "CFR Request",
            isCollection: false,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "CFR Category is required",
                parameters: null, property: "cfrCategoryId",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Title is required",
                parameters: null, property: "title",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Description is required",
                parameters: null, property: "description",
                validationType: ValidationTypeEnum.Required
            }]
        };

        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    async next() {
        if (this.currentStep == 1) {
            const valid = await this.validator.validate();
            if (valid.valid) {
                await this.save();
                if (!this.isEdit) {
                    this.currentStep = 2;
                } else {
                    this.notificationClientService.show("Condition for review", "Successfully updated", NotificationType.Success);
                    this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Cfr, { id: this.cfrId });
                }
            }
        }
        else if (this.currentStep == 2) {
            this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Cfr, { id: this.cfrId });
        }
    }

    goBack() {
        this.cfrId = null;
        this.router.navigateBack();
    }

    async newIncident() {
        await this.save();
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddIncident, {
            cfrId: this.cfrId,
            id: 0,
            vesselId: this.vessel.vesselId,
            internationalMaritimeOrganizationNumber:
                this.vessel.internationalMaritimeOrganizationNumber ? this.vessel.internationalMaritimeOrganizationNumber : '0',
            vesselName: this.vessel.name
        })
    }

    async linkToIncident() {
        try {
            if (!this.cfrId) {
                await this.save(false);
            }

            const inc = await this.cfrService.linkToIncident(this.cfrId, this.incidentIds);
            this.incidentLinkingVisible = false;
            for (const i of inc) {
                this.linkedIncidents.push(i);
            }
            this.incidentIds = [];
        } catch (e) {
            this.notificationClientService.show("Failed to link incident", "", NotificationType.Error);
            throw e;
        }
    }

    async unlinkIncident(incidentId: number) {
        try {
            const promise: Promise<void> = this.cfrService.unlinkIncident(this.cfrId, incidentId);
            await promise;

            const incs = this.linkedIncidents.filter(x => x.incidentId == incidentId);

            for (const i of incs) {
                const index = this.linkedIncidents.indexOf(i);
                if (index >= 0)
                    this.linkedIncidents.splice(index, 1);
            }

        } catch (e) {
            this.notificationClientService.show("Failed to un-link incident", "", NotificationType.Error);
        }
    }

    @computedFrom(Reflection.getPropertyPath<NewCfr>(z => z.linkedIncidents),
        Reflection.getPropertyPath<NewCfr>(z => z.linkedIncidents.length))
    get linkedIncidentId() {
        return this.linkedIncidents.map(x => x.incidentId);
    }

    async newPscInspection() {
        await this.save();
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddPsc, {
            cfrId: this.cfrId,
            id: 0,
            vesselId: this.vessel.vesselId,
            internationalMaritimeOrganizationNumber:
                this.vessel.internationalMaritimeOrganizationNumber ? this.vessel.internationalMaritimeOrganizationNumber : '0',
            vesselName: this.vessel.name
        })
    }

    async linkToPscInspection() {
        try {
            if (!this.cfrId)
                await this.save(false);

            const promise: Promise<LinkedPscInspectionModel[]> = this.cfrService.linkToPscInspection(this.cfrId, this.pscInspectionIds);
            const psc = await promise;

            this.pscInspectionLinkingVisible = false;
            for (const i of psc)
                this.linkedPscInspections.push(i);
            this.pscInspectionIds = [];
        } catch (e) {
            this.notificationClientService.show("Failed to link psc inspection", "", NotificationType.Error);
        }
    }

    async unlinkPscInspection(pscInspectionId: number) {
        try {
            const promise: Promise<void> = this.cfrService.unlinkPscInspection(this.cfrId, pscInspectionId);
            await promise;

            const incs = this.linkedPscInspections.filter(x => x.pscInspectionId == pscInspectionId);

            for (const i of incs) {
                const index = this.linkedPscInspections.indexOf(i);
                if (index >= 0)
                    this.linkedPscInspections.splice(index, 1);
            }

        } catch (e) {
            this.notificationClientService.show("Failed to un-link psc inspection", "", NotificationType.Error);
        }
    }

    @computedFrom(Reflection.getPropertyPath<NewCfr>(z => z.linkedPscInspections),
        Reflection.getPropertyPath<NewCfr>(z => z.linkedPscInspections.length))
    get linkedPscInspectionId() {
        return this.linkedPscInspections.map(x => x.pscInspectionId);
    }

    showIncidentLinking() {
        this.incidentLinkingVisible = true;
    }

    cancelIncidentLinking() {
        this.incidentLinkingVisible = false;
    }

    showPscInspectionLinking() {
        this.pscInspectionLinkingVisible = true;
    }

    cancelPscInspectionLinking() {
        this.pscInspectionLinkingVisible = false;
    }

    onCfrCategoriesLoaded(values: LookupValue[]) {
        if (this.fbrItemId > 0 && values && values.length > 0 && !this.cfrId) {
            const fbrCategories = values.filter(x => x.additionalData && x.additionalData.useForFeedbackReport);
            this.cfrCategoryId = fbrCategories && fbrCategories.length > 0 ? fbrCategories[0].id : null;
        }
    }

    removeAttachment(index: number) {
        if (this.attachmentsToCopy && this.attachmentsToCopy.length > index) {
            this.attachmentsToCopy.splice(index, 1);
        }
    }
}
